import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    FormControlLabel,
    Switch
} from '@mui/material';
import { Plus, Trash2, SquarePen, Eye, X } from 'lucide-react';
import { getAirports, createAirport, updateAirport, deleteAirport, getAirportById } from '../../../Services/AddRetreatManagement/RetreatDetailsApi';
import DeleteModal from '../../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../../Components/Common/GlobalSanckbar';
import GlobalPagination from '../../../Components/Common/Globalpagination';


const StatusBadge = ({ isActive }) => (
    <Box sx={{
        display: 'inline-block',
        px: 1.5, py: 0.5,
        borderRadius: '4px',
        bgcolor: isActive ? '#E8F5E9' : '#FEECEB',
        color: isActive ? '#2E7D32' : '#F44336',
        fontSize: '0.7rem',
        fontWeight: 600
    }}>
        {isActive ? "Active" : "Inactive"}
    </Box>
);

const GetAirports = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [airports, setAirports] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [formData, setFormData] = useState({
        airportName: '',
        iataCode: '',
        location: '',
        displayOrder: '1',
        isActive: true
    });
    const [currentItem, setCurrentItem] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    // Pagination and search states
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [limit, setLimit] = useState(25);

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // View modal states
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedAirport, setSelectedAirport] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);

    const handleOpenViewModal = async (item) => {
        setSelectedAirport(item);
        setViewModalOpen(true);
        if (item?.id) {
            try {
                setViewLoading(true);
                const response = await getAirportById(item.id);
                if (response && (response.success || response.data)) {
                    const data = response.data || response;
                    if (data && typeof data === 'object') {
                        setSelectedAirport(prev => ({ ...prev, ...data }));
                    }
                }
            } catch (err) {
                console.error("Error fetching airport details:", err);
            } finally {
                setViewLoading(false);
            }
        }
    };

    const handleCloseViewModal = () => {
        setViewModalOpen(false);
        setSelectedAirport(null);
        setViewLoading(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const fetchAirports = useCallback(async (currentPage = 1, currentLimit = limit, searchVal = '') => {
        try {
            setLoading(true);
            const response = await getAirports(currentPage, currentLimit, searchVal);
            if (response && response.success) {
                setAirports(response.data || []);
                setTotalPages(response.totalPages || 1);
                setTotalRecords(response.totalRecords ?? response.total ?? ((response.totalPages || 1) * currentLimit));
                setPage(response.currentPage || currentPage);
            } else {
                setAirports([]);
                setTotalRecords(0);
                setError("Failed to load airports.");
            }
        } catch (err) {
            setAirports([]);
            setTotalRecords(0);
            setError("Failed to fetch airports. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, [limit]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchAirports(page, limit, searchQuery);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [fetchAirports, page, limit, searchQuery]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPage(1);
    };

    const handleOpenModal = (item = null) => {
        if (item) {
            setIsEdit(true);
            setCurrentItem(item);
            setFormData({
                airportName: item.airportName || '',
                iataCode: item.iataCode || '',
                location: item.location || '',
                displayOrder: item.displayOrder !== undefined && item.displayOrder !== null && item.displayOrder !== '' ? item.displayOrder : '1',
                isActive: item.isActive ?? true
            });
        } else {
            setIsEdit(false);
            setCurrentItem(null);
            setFormData({
                airportName: '',
                iataCode: '',
                location: '',
                displayOrder: '1',
                isActive: true
            });
        }
        setValidationErrors({});
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEdit(false);
        setCurrentItem(null);
        setFormData({ airportName: '', iataCode: '', location: '', displayOrder: '1', isActive: true });
        setValidationErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let finalValue = value;
        if (name === 'displayOrder') {
            if (value !== '') {
                const num = parseInt(value, 10);
                if (num > 999) finalValue = 999;
                else if (num < 1) finalValue = 1;
                else finalValue = num;
            }
        }
        setFormData(prev => ({
            ...prev,
            [name]: finalValue
        }));
        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSwitchChange = (e) => {
        const { name, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleSubmit = async () => {
        const errors = {};
        if (!formData.airportName.trim()) {
            errors.airportName = "Airport name is required";
        }
        if (!formData.iataCode.trim()) {
            errors.iataCode = "IATA code is required";
        }
        if (!formData.location.trim()) {
            errors.location = "Location is required";
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        setSubmitting(true);
        setError(null);
        setValidationErrors({});

        const payload = {
            ...formData,
            airportName: formData.airportName.trim(),
            iataCode: formData.iataCode.trim().toUpperCase(),
            location: formData.location.trim(),
            displayOrder: formData.displayOrder !== "" ? Number(formData.displayOrder) : undefined,
            isActive: Boolean(formData.isActive)
        };

        try {
            if (isEdit) {
                await updateAirport(currentItem.id, payload);
            } else {
                await createAirport(payload);
            }

            handleCloseModal();
            fetchAirports(isEdit ? page : 1, searchQuery);
            showSnackbar(`Airport ${isEdit ? 'updated' : 'created'} successfully!`, 'success');
        } catch (err) {
            const serverError = err.response?.data;
            const errorMessage = serverError?.message || `Failed to ${isEdit ? 'update' : 'create'} airport`;
            if (serverError && serverError.details) {
                const fieldErrors = {};
                serverError.details.forEach(detail => {
                    const fieldName = detail.field === 'general' ? 'airportName' : detail.field;
                    fieldErrors[fieldName] = detail.message;
                });
                setValidationErrors(fieldErrors);
            } else {
                setValidationErrors({ airportName: errorMessage });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const response = await deleteAirport(selectedId);
                if (response && response.success) {
                    showSnackbar("Airport deleted successfully!", "success");
                    setDeleteModalOpen(false);
                    setSelectedId(null);
                    fetchAirports(page, searchQuery);
                } else {
                    const serverError = response;
                    const errorMsg = Array.isArray(serverError?.errors)
                        ? serverError.errors.join(", ")
                        : (serverError?.details?.[0]?.message || serverError?.message || "Failed to delete airport");
                    setDeleteError(errorMsg);
                }
            } catch (err) {
                console.error("Delete Error:", err);
                const serverError = err.response?.data;
                const errorMessage = Array.isArray(serverError?.errors)
                    ? serverError.errors.join(", ")
                    : (serverError?.details?.[0]?.message || serverError?.message || err.message || "Failed to delete airport");
                setDeleteError(errorMessage);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4, width: '100%', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Airports
                    </Typography>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
                        <TextField
                            placeholder="Search airports..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            variant="outlined"
                            size="small"
                            sx={{
                                width: { xs: '100%', sm: '250px' },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    '& fieldset': {
                                        borderColor: '#E8E8E8',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#FFBD65',
                                    },
                                },
                            }}
                        />

                        <PermissionWrapper permission="create" mode="disable">
                            <Button
                                disableElevation
                                variant="contained"
                                startIcon={<Plus size={20} />}
                                onClick={() => handleOpenModal()}
                                sx={{ bgcolor: '#FFBD65', px: 3, color: '#fff', borderRadius: '8px', height: '40px', width: { xs: '100%', sm: 'auto' }, whiteSpace: 'nowrap' }}
                            >
                                Add Airport
                            </Button>
                        </PermissionWrapper>
                    </Stack>
                </Stack>

                {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 950 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Airport Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>IATA Code</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Location</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '110px' }}>Display Order</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px', textAlign: 'right' }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 10 }}>
                                            <CircularProgress sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : airports.length > 0 ? (
                                    airports.map((item, idx) => (
                                        <TableRow key={item.id} sx={{
                                            transition: 'background-color 0.2s',
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell sx={{ color: '#666', py: 1.25, px: 1.5, fontSize: '0.8rem', textAlign: 'center' }}>{(page - 1) * limit + idx + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>{item.airportName}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', fontWeight: 600, color: '#444' }}>{item.iataCode}</TableCell>
                                            <TableCell sx={{ color: '#666', py: 1.25, px: 1.5, fontSize: '0.8rem' }}>{item.location}</TableCell>
                                            <TableCell sx={{ color: '#666', py: 1.25, px: 1.5, fontSize: '0.8rem' }}>{item.displayOrder !== undefined && item.displayOrder !== null ? item.displayOrder : '—'}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <StatusBadge isActive={item.isActive} />
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.cuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.uuName || '-'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    <Tooltip title="View">
                                                        <IconButton size="small" sx={{ color: '#64748B' }} onClick={() => handleOpenViewModal(item)}>
                                                            <Eye size={16} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton size="small" sx={{ color: '#FFBD65' }} onClick={() => handleOpenModal(item)}>
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small" sx={{ color: '#FF4D49' }} onClick={() => handleDeleteClick(item.id)}>
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 3, color: '#AAA', fontSize: '0.8rem' }}>
                                            No airports found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Pagination */}
                <GlobalPagination
                    page={page}
                    pageSize={limit}
                    totalRecords={totalRecords}
                    totalPages={totalPages}
                    onPageChange={(newPage) => setPage(newPage)}
                    onPageSizeChange={(newLimit) => {
                        setLimit(newLimit);
                        setPage(1);
                    }}
                    loading={loading}
                />

                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222' }}>
                        {isEdit ? 'Edit Airport' : 'Add Airport'}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2.5} sx={{ mt: 1 }}>
                            <TextField
                                fullWidth
                                label="Airport Name *"
                                name="airportName"
                                value={formData.airportName}
                                onChange={handleInputChange}
                                placeholder="e.g. Dehradun Airport"
                                autoFocus
                                error={!!validationErrors.airportName}
                                helperText={
                                    validationErrors.airportName ||
                                    (formData.airportName && (formData.airportName.includes('(') || formData.airportName.includes(')'))
                                        ? "Only airport name is enough (e.g. Dehradun Airport), keep it small and neat"
                                        : "e.g. Dehradun Airport"
                                    )
                                }
                            />
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="IATA Code *"
                                        name="iataCode" size="small"
                                        value={formData.iataCode}
                                        onChange={handleInputChange}
                                        placeholder="e.g. DEL"
                                        inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
                                        error={!!validationErrors.iataCode}
                                        helperText={validationErrors.iataCode}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Location *"
                                        name="location" size="small"
                                        value={formData.location}
                                        onChange={handleInputChange}
                                        placeholder="e.g. Kochi"
                                        error={!!validationErrors.location}
                                        helperText={
                                            validationErrors.location ||
                                            (formData.location && (formData.location.includes(',') || formData.location.toLowerCase().includes('kerala') || formData.location.toLowerCase().includes('india'))
                                                ? "Only city name is enough (e.g. Kochi), keep it small and neat"
                                                : "e.g. Dehradun"
                                            )
                                        }
                                    />
                                </Grid>
                            </Grid>

                            <Grid container spacing={2} alignItems="center">
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Display Order"
                                        name="displayOrder"
                                        type="number"
                                        size="small"
                                        value={formData.displayOrder}
                                        onChange={handleInputChange}
                                        placeholder="e.g. 1"
                                        inputProps={{ min: 1, max: 999 }}
                                        error={!!validationErrors.displayOrder}
                                        helperText={validationErrors.displayOrder || "Optional order number (1-999)"}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.isActive}
                                                onChange={handleSwitchChange}
                                                name="isActive"
                                                color="primary"
                                            />
                                        }
                                        label={<Typography sx={{ fontSize: '0.85rem', color: '#444' }}>Active</Typography>}
                                    />
                                </Grid>
                            </Grid>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 3 }}>
                        <Button onClick={handleCloseModal} sx={{ color: '#222' }}>Cancel</Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={submitting}
                            variant="contained"
                            sx={{ bgcolor: '#FFBD65', color: '#fff' }}
                        >
                            {submitting ? <CircularProgress size={24} color="inherit" /> : (isEdit ? 'Update' : 'Add')}
                        </Button>
                    </DialogActions>
                </Dialog>

                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleConfirmDelete}
                    title="Delete Airport"
                    description="Are you sure you want to delete this airport? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />

                {/* VIEW AIRPORT DETAILS DIALOG */}
                <Dialog
                    open={viewModalOpen}
                    onClose={handleCloseViewModal}
                    maxWidth="sm"
                    fullWidth
                    scroll="paper"
                    PaperProps={{
                        sx: {
                            borderRadius: '8px',
                            border: '1px solid #E2E8F0',
                            boxShadow: 'none',
                            bgcolor: '#FFFFFF'
                        }
                    }}
                >
                    <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E2E8F0' }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1E293B' }}>
                            Airport Details
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={handleCloseViewModal}
                            sx={{ color: '#64748B' }}
                        >
                            <X size={18} />
                        </IconButton>
                    </Box>

                    <DialogContent sx={{ p: 2.5 }}>
                        {viewLoading && !selectedAirport ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                            </Box>
                        ) : selectedAirport ? (
                            <Stack spacing={2.5}>
                                {/* Airport Information Section */}
                                <Box>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.25 }}>
                                        Airport Information
                                    </Typography>
                                    <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', p: 2 }}>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748B', mb: 0.25 }}>
                                                    Airport Name
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                                    {selectedAirport.airportName || '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748B', mb: 0.25 }}>
                                                    IATA Code
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1E293B' }}>
                                                    {selectedAirport.iataCode || '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748B', mb: 0.25 }}>
                                                    Location
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                                    {selectedAirport.location || '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748B', mb: 0.25 }}>
                                                    Display Order
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                                    {selectedAirport.displayOrder !== undefined && selectedAirport.displayOrder !== null ? selectedAirport.displayOrder : '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748B', mb: 0.5 }}>
                                                    Status
                                                </Typography>
                                                <StatusBadge isActive={selectedAirport.isActive} />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Box>

                                {/* Audit Information Section */}
                                <Box>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.25 }}>
                                        Audit Information
                                    </Typography>
                                    <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', p: 2 }}>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748B', mb: 0.25 }}>
                                                    Created By
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                                    {selectedAirport.cuName || '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748B', mb: 0.25 }}>
                                                    Updated By
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                                    {selectedAirport.uuName || '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748B', mb: 0.25 }}>
                                                    Created At
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                                    {formatDate(selectedAirport.createdAt)}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748B', mb: 0.25 }}>
                                                    Updated At
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                                    {formatDate(selectedAirport.updatedAt)}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Box>
                            </Stack>
                        ) : (
                            <Typography sx={{ color: '#64748B', fontSize: '0.85rem', textAlign: 'center', py: 4 }}>
                                No details available.
                            </Typography>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #E2E8F0' }}>
                        <Button
                            onClick={handleCloseViewModal}
                            variant="outlined"
                            sx={{
                                color: '#1E293B',
                                borderColor: '#E2E8F0',
                                textTransform: 'none',
                                fontSize: '0.82rem',
                                fontWeight: 500,
                                borderRadius: '6px',
                                boxShadow: 'none',
                                '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' }
                            }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </PermissionWrapper>
    );
};

export default GetAirports;

